/**
 * Reminder source-field helpers.
 * Canonical list lives in DB table `reminder_source_fields` (maintained by developers).
 */

export type ReminderSourceField = {
  source_field: string;
  category: 'driver' | 'vehicle' | 'fleet';
  subject_kind: 'driver' | 'vehicle' | 'fleet_operator';
  table_name: 'drivers' | 'vehicles' | 'fleet_operators';
  due_column: string;
  select_extra: string[];
  rule_kinds: Array<'ahead_days' | 'interval_ahead'>;
  default_interval_days?: number | null;
  next_due_column?: string | null;
  driver_in_app: boolean;
  label_en: string;
  label_zh: string;
  enabled: boolean;
  sort_order: number;
  created_at?: string;
  updated_at?: string;
};

/** @deprecated Use ReminderSourceField — kept for call-site compatibility */
export type ReminderSourceAllowlistEntry = ReminderSourceField & {
  table?: ReminderSourceField['table_name'];
  label?: (row: Record<string, unknown>) => string;
};

export function subjectLabelFromSource(
  source: Pick<ReminderSourceField, 'table_name'>,
  row: Record<string, unknown>
): string {
  if (source.table_name === 'drivers' || source.table_name === 'fleet_operators') {
    return String(row.name || row.id || '');
  }
  const plate = String(row.registration_plate || '').trim();
  const fleet = String(row.fleet_number || '').trim();
  return plate || fleet || String(row.id || '');
}

export function filterSourcesByCategory(
  sources: ReminderSourceField[],
  category: 'driver' | 'vehicle' | 'fleet',
  onlyEnabled = true
): ReminderSourceField[] {
  return sources.filter(
    (s) => s.category === category && (!onlyEnabled || s.enabled)
  );
}

export function findSourceField(
  sources: ReminderSourceField[],
  sourceField: string
): ReminderSourceField | undefined {
  return sources.find((s) => s.source_field === sourceField);
}

export function isDriverInAppSourceRow(
  sources: ReminderSourceField[],
  sourceField: string
): boolean {
  return !!findSourceField(sources, sourceField)?.driver_in_app;
}

export function slugifyReminderCode(nameEn: string): string {
  const base = nameEn
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '')
    .slice(0, 40);
  const rand = Math.random().toString(36).slice(2, 6);
  return `custom_${base || 'type'}_${rand}`;
}

/** Safe identifier check before interpolating column names from DB. */
export function isSafeSqlIdent(name: string): boolean {
  return /^[a-z_][a-z0-9_]*$/.test(name);
}
